import {useApolloClient} from '@apollo/client';
import {TransferType} from '@transactions/graphql';
import {Merge} from 'lucide-react';
import * as React from 'react';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {totalsByCurrency} from 'src/lib/currencyTotals';
import {useModals} from 'src/Modals';
import PopoverMenuItem from 'src/PopoverMenuItem';
import {SelectionBar} from 'src/SelectionBar';
import type {UnifiedTransactionFragment} from '@transactions/graphql';

/**
 * The Transactions list's selection bar: Edit as the primary (the edit sheet
 * for one, the bulk editor for several), Merge under More.
 */
export const TransactionSelectionBar = ({
  open,
  selected,
  onDone,
}: {
  open: boolean;
  selected: UnifiedTransactionFragment[];
  onDone: () => void;
}) => {
  const modals = useModals();
  const client = useApolloClient();
  const formatMoney = useMoneyFormatter();
  const sums = totalsByCurrency(
    selected.map((tx) => ({amount: tx.netAmount, currency: tx.currency})),
  );
  // Merged transactions must share one currency, and conversions can't merge
  // at all (their lines span two currencies). The server rejects both.
  const hasConversion = selected.some(
    (tx) => tx.transferType === TransferType.Conversion,
  );
  const canMerge = selected.length >= 2 && sums.length === 1 && !hasConversion;

  // A save can move rows out of the filters the list was loaded with (a new
  // category, tag, or note), and Apollo won't recompute a cached list's
  // membership, so the list refetches. The selection holds the pre-save
  // fragments, so it ends too — a second edit from them would write stale
  // values back.
  const handleSaved = () => {
    client.cache.evict({id: 'ROOT_QUERY', fieldName: 'transactions'});
    client.cache.gc();
    onDone();
  };

  const edit = () => {
    if (selected.length === 1) {
      modals.editTransactionModal.openModal({
        transaction: selected[0],
        onSaved: handleSaved,
      });
    } else if (selected.length > 1) {
      modals.bulkEditTransactionsModal.openModal({
        transactions: selected,
        onSaved: handleSaved,
      });
    }
  };

  return (
    <SelectionBar
      open={open}
      count={selected.length}
      total={sums.map((sum) => formatMoney(sum)).join(' · ')}
      menuItems={
        <PopoverMenuItem
          onSelect={() =>
            modals.mergeTransactionsModal.openModal({
              transactions: selected,
              onSuccess: onDone,
            })
          }
          disabled={!canMerge}
          iconLeft={<Merge />}
        >
          Merge
        </PopoverMenuItem>
      }
      primary={{label: 'Edit', onClick: edit}}
      onCancel={onDone}
    />
  );
};
